import { Modal } from '@fozito/common-ui';
import { Form } from 'antd';
import { delay, set } from 'lodash';
import { PAYMENT_TYPE } from 'modules/POS/Cashier/cashier.constants';
import { cashierCtrl } from 'modules/POS/Cashier/cashier.controller';
import { IUICashierFormValues } from 'modules/POS/Cashier/cashier.interface';
import useListenRequestSync from 'modules/POS/Cashier/hooks/useListenRequestSync';
import useSMSEmailSignature from 'modules/POS/Cashier/hooks/useSMSEmailSignature';
import { useSocketContext } from 'modules/POS/Cashier/hooks/useSocket';
import { ICustomerPaymentDetail, PaymentSocketData, TypePaymentActors, TypePaymentPassData } from 'modules/POS/Cashier/socket.interface';
import CardPaymentDetail from 'modules/POS/PaymentDetails/CardPaymentDetail';
import useConfirmTipTicket from 'modules/POS/PaymentDetails/ConfirmTipTicket';
import { useTicketWrapper } from 'modules/POS/Receipts/context';
import cashierService from 'modules/POS/services';
import cashierApis from 'modules/POS/services/apis';
import { IBillInfoResData } from 'modules/POS/services/types/bill';
import { ICashierCreditCardPayment } from 'modules/POS/services/types/payment';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { IResponseData } from 'services/response';
import { Message } from 'stompjs';
import { removeBase64Prefix } from 'utils/removeBase64Prefix';
import storage from 'utils/sessionStorage';
import { PAYMENT_PASS_DATA_TOPIC } from 'utils/socket';
import { setPageLoading } from 'widgets/PageLoading';
import CreditCardLoading, { ICreditPaymentLoadingRef } from './PaymentLoading';
import RequestSignature, { ISignatureRef } from './RequestSignature';

export type ICashierCardPaymentMethod = (billId: string, payment: ICashierCreditCardPayment) => Promise<boolean>;
export enum CREDIT_CARD_COMPLETED_TICKET {
  NO_RECEIPT = 'NO_RECEIPT',
  EMAIL = 'EMAIL',
  SMS = 'SMS',
  PRINT = 'PRINT',
}
type Props = {
  onPayment: ICashierCardPaymentMethod;
  onCancel: () => void;
  onCompletedTicket: (type: CREDIT_CARD_COMPLETED_TICKET) => void
  enableCardFee?: IUICashierFormValues['chargeFee'];
  feeCreditCard?: IUICashierFormValues['feeCreditCard'];
  feeCreditCardAmount?: IUICashierFormValues['feeCreditCardAmount'];
};
type Ref = {
  open: (billId: string) => void;
};
export const useModalCardPaymentRef = () => useRef<Ref>(null);
const ModalCardPayment = forwardRef<Ref, Props>(({ onPayment, onCancel, enableCardFee, feeCreditCardAmount }, ref) => {
  const [completedTicketModalRef, completedTicketModalContext] = useConfirmTipTicket();
  const [form] = Form.useForm();
  const LoadingRef = useRef<ICreditPaymentLoadingRef>(null);
  const signatureRef = useRef<ISignatureRef>(null);
  const [billId, setOpen] = useState<string>('');
  const SMSEmailSignature = useSMSEmailSignature();
  const socketContext = useSocketContext();
  const ticketContext = useTicketWrapper();
  const cashierMsg = cashierService.selectors.cashierMsg();

  const sendingData = (type: string, data?: any) => {
    const body: PaymentSocketData = {
      storeId: storage.shop_id.get(),
      billId: billId || '',
      actor: TypePaymentActors.CASHIER,
      data,
    };
    switch (type) {
      case TypePaymentPassData.REQUEST_SIGNATURE: {
        set(body, 'action', TypePaymentPassData.REQUEST_SIGNATURE);
        break;
      }
      case TypePaymentPassData.PAYMENT_PROCESSING: {
        set(body, 'action', TypePaymentPassData.PAYMENT_PROCESSING);
        break;
      }
      case TypePaymentPassData.PAYMENT_PROCESSING_FAIL: {
        set(body, 'action', TypePaymentPassData.PAYMENT_PROCESSING_FAIL);
        break;
      }
      default:
        break;
    }

    socketContext.send(PAYMENT_PASS_DATA_TOPIC, body);
  };


  const listeningData = (message: Message) => {
    if (!message.body) return;
    const payment: PaymentSocketData = JSON.parse(message.body);
    if (!payment) return;
    if (payment.billId !== billId) return;
    if (payment.actor !== TypePaymentActors.CUSTOMER) return;

    switch (payment.action) {
      case TypePaymentPassData.COMPLETED_PAYMENT: {
        signatureRef.current?.close();
        onDone();
        break;
      }
      case TypePaymentPassData.COMPLETED_PAYMENT_WITHOUT_RECEIPT: {
        signatureRef.current?.close();
        onDone(true);
        break;
      }
      case TypePaymentPassData.ON_CUSTOMER_SIGNATURE_CHANGE: {
        signatureRef.current?.setValue(payment.data);
        break;
      }
      case TypePaymentPassData.CONFIRM_CORRECT_CC: {
        delay(() => form.submit(), 100);
        break;
      }
      default:
        break;
    }
  };

  useEffect(() => {
    if (!cashierMsg) return;
    listeningData(cashierMsg);
  }, [cashierMsg]);

  const onClose = () => {
    socketContext.backToCheckIn();
    setOpen('');
  };

  const fetchDetail = async (_billId: string) => {
    try {
      const res: IResponseData<IBillInfoResData> = await cashierApis.getBillById(_billId);
      const detail = res.data.data;
      if (detail) {
        cashierCtrl.switchTicketWithDetailData(detail);
        const items = detail?.items?.map((o) => ({
          name: o.itemName,
          price: o.price,
          quantity: o.quantity || 0,
          discount: o.discount || 0,
          requiredMember: o.requiredMember,
        })) || [];
        const amountFinal = detail.total + (feeCreditCardAmount || 0);
        const payments = {
          cash: 0,
          remaining: 0,
          chanceCash: 0,
          loyalty: 0,
          giftCard: 0,
          checkOthers: 0,
          card: amountFinal,
          multiPay: 0,
          ebtFood: 0,
          isSingleCard: true,

          totalValue: amountFinal,
          subTotal: detail.subTotal || 0,
          cardFee: feeCreditCardAmount,
          saleTax: detail.saleTax || 0,
          usedTax: detail.useTax || 0,
          discount: (detail.discount || 0) + (detail.discountItem || 0),

        } as ICustomerPaymentDetail;
        form.setFieldsValue({
          detail,
          items,
          amount: amountFinal,
          payments
        });

        setTimeout(() => {
          socketContext.send(PAYMENT_PASS_DATA_TOPIC, {
            billId,
            actor: TypePaymentActors.CASHIER,
            action: TypePaymentPassData.PASS_DATA,
            data: payments as ICustomerPaymentDetail,
          });
        }, 100);
        return detail;
      }
      return null;
    } catch (error) {
      return null;
    }
  };

  useListenRequestSync(async (_billId) => {
    if (!billId) return;
    await fetchDetail(_billId);
  });

  const onOpen: Ref['open'] = async (billId) => {
    socketContext.switchCusPayment(billId);
    try {
      const detail = await fetchDetail(billId);
      if (detail) {
        setOpen(billId);
      }
    } catch (error) {
    }
  };

  const _onCancel = () => {
    socketContext.backToCheckIn();
    setOpen('');
    onCancel();
  };

  const requestSignature = () => {
    signatureRef.current?.open();
    sendingData(TypePaymentPassData.REQUEST_SIGNATURE);
  };

  const handleFinish = async (values: any) => {
    const data = values as { amount: number, detail: IBillInfoResData };
    if (!billId) return;
    const payment: ICashierCreditCardPayment = {
      amount: data.amount,
      paymentType: PAYMENT_TYPE.CREDIT_CARD,
      chargeFee: enableCardFee,
      feeCredit: feeCreditCardAmount,
    };
    LoadingRef.current?.open(data.amount, 120000);
    sendingData(TypePaymentPassData.PAYMENT_PROCESSING, { amount: data.amount || 0 });
    try {
      const isSuccess = await onPayment(billId, payment);
      if (!isSuccess) return sendingData(TypePaymentPassData.PAYMENT_PROCESSING_FAIL);
      requestSignature();
    } catch (error) {
      sendingData(TypePaymentPassData.PAYMENT_PROCESSING_FAIL);
    } finally {
      LoadingRef.current?.close();
    }
  };
  // complete ticket print
  const onDone = (ignorePrint?: boolean) => {
    if (!billId) return;
    socketContext.alertPaymentSuccess(billId);
    const callback = () => {
      cashierCtrl.generateNewTicketAfterPayment();
      setOpen('');
    };
    if (!completedTicketModalRef.current) {
      callback();
      if (ignorePrint) {
        ticketContext.doPrintNoReceipt(billId, true);
        return;
      }
      ticketContext.completedTicket(billId, true);
      return;
    }
    completedTicketModalRef.current.open(billId, callback, ignorePrint);
  };

  const onSignatureDone = async (signatureValue: string) => {
    const detail = form.getFieldValue('detail') as IBillInfoResData;
    setPageLoading(true);
    try {
      const res: IResponseData<boolean> = await cashierApis.updateSignature(detail?.billId || '', removeBase64Prefix(signatureValue));
      if (res.data.data) {
        onDone();
        signatureRef.current?.close();
        return;
      }
    } catch (error) { }
    finally {
      setPageLoading(false);
    }
  };
  useImperativeHandle(ref, () => ({
    open: onOpen
  }));

  return (
    <Modal title='Card' open={!!billId} onClose={onClose} width={'100vw'} styles={{
      content: { height: '100vh', boxShadow: 'none' },
      header: { display: 'none' },
      body: { height: '100vh', maxHeight: 'unset' },
      wrapper: { background: '#fff' },
      mask: { display: 'none' }
    }}>
      <Form form={form} onFinish={handleFinish} style={{ height: '100%' }}>
        <Form.Item name={'detail'} noStyle />
        <Form.Item name={'amount'} noStyle />
        <Form.Item name={'items'} noStyle />
        <Form.Item name={'payments'} noStyle />
        <Form.Item noStyle shouldUpdate>
          {({ getFieldValue }) => {
            const detail: IBillInfoResData = getFieldValue('detail');
            const items = getFieldValue('items');
            const payments: ICustomerPaymentDetail = getFieldValue('payments');
            const amount = getFieldValue('amount');
            return (
              <CardPaymentDetail
                date={detail?.createdDated || ''}
                customerName={detail?.customerName || 'Unknown Customer'}
                customerPhone={detail?.customerPhone}
                ticketNumber={detail?.ticketNumber || 0}
                items={items}
                subtotal={payments?.subTotal || 0}
                cardFee={payments?.cardFee || 0}
                saleTax={payments?.saleTax || 0}
                useTax={payments?.usedTax || 0}
                discount={payments?.discount || 0}
                payments={payments}
                loyaltyAvailable={detail?.customerPoint || 0}
                total={amount}
                onCancel={_onCancel}
                onConfirm={() => form.submit()}
              />
            );
          }}
        </Form.Item>
        <CreditCardLoading ref={LoadingRef} />
        <Form.Item shouldUpdate noStyle>
          {({ getFieldValue }) => {
            const detail = getFieldValue('detail') as IBillInfoResData;
            return (
              <RequestSignature ref={signatureRef}
                onAgree={onSignatureDone}
                onNoReceipt={SMSEmailSignature.onNoReceipt(detail?.billId || '', () => onDone(true))}
                onEmail={SMSEmailSignature.onEmail(detail?.billId || '', () => onDone(true))}
                onSMS={SMSEmailSignature.onSMS(detail?.billId || '', () => onDone(true))}
                defaultSMSPhone={detail?.customerPhone}
              />
            );
          }}
        </Form.Item>
      </Form>
      {completedTicketModalContext}
    </Modal>
  );
});
ModalCardPayment.displayName = 'ModalCardPayment';
export default ModalCardPayment;